import React, {ReactNode, useEffect, useState} from "react";
import {makeStyles, Theme} from "@material-ui/core/styles";
import {
    Backdrop,
    Box,
    Button,
    Checkbox,
    Container, Dialog, Fade,
    FormControlLabel,
    Grid, Modal,
    Typography
} from "@material-ui/core";
import {ReactComponent as CheckboxBorder} from "../../Images/checkboxBorder.svg";
import CheckboxChecked from "../../Components/CheckboxChecked";
import AgoraRTC from "agora-rtc-sdk-ng";
import MediaPlayer from "../MediaPlayer";
import useAgora from "../../hooks/useAgora";

const useStyle = makeStyles((theme: Theme) => ({
    buttonContainer: {
        margin: "0 auto",
        width: 350,
    },
    modal: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        [theme.breakpoints.down("sm")]: {
            width: "99%",
            margin: "0 auto"
        }
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 2),
        display: "flex",
        flexDirection: "column"
    },
    button: {
        width: 110,
        marginTop: 10,
        marginBottom: 10
    },
    incomminVideoContainer: {
        position: "relative",
        display: "flex",
        justifyContent: "center",
        minWidth: 300,
        minHeight: 300,
        width: "50%",
        margin: "20px auto 0",
        backgroundColor: "#000",
        [theme.breakpoints.down("xs")]: {
            position: "fixed",
            width: "100%",
            maxHeight: "80%",
            height: "60%",
            top: "60px",
            margin: 0,
        }
    },
    incomminTestVideoContainer: {
        [theme.breakpoints.down("xs")]: {
            position: "static",
            width: "100%",
            maxHeight: "80%",
            height: "60%",
            margin: 0,
        },
    }
}));


interface IInputDevices {
    value: string,
    label: string,
}

interface SoundAndCameraCheckProps {
    startChat: (audioDevice: string, videoDevice: string) => void,
}

const client = AgoraRTC.createClient({codec: "vp8", mode: "rtc"});

const SoundAndCameraCheck: React.FC<SoundAndCameraCheckProps> = (
    {
        startChat,
    }) => {
    const classes = useStyle();
    const [level, setLevel] = useState(0);
    const [showContactUs, setShowContactUs] = useState(false);
    const appid = process.env.REACT_APP_AGORA_APP_ID;

    const {
        localAudioTrack,
        localVideoTrack,
        leave,
        join,
        joinState,
        remoteUsers,
        audioDevices,
        videoDevices,
        audioDevice,
        videoDevice,
        updateAudioInput,
        updateVideoInput,
    } = useAgora(client);

    useEffect(() => {
        join(appid, "test-channel");
    }, []);

    useEffect(() => {
        if (localAudioTrack) {
            const checkInputSound = setInterval(() => {
                const level = localAudioTrack.getVolumeLevel();
                setLevel(Math.trunc(level * 10));
            }, 50);
            return () => clearInterval(checkInputSound);
        }
    }, [localAudioTrack]);

    const enterChat = () => {
        leave();
        startChat(audioDevice, videoDevice);
    };

    return (
        <Dialog fullScreen open={true}>
            <Grid container>
                <Grid item xs={12}>
                    <Box marginBottom="15px">
                        <div
                            className={`${classes.incomminVideoContainer} ${classes.incomminTestVideoContainer}`}
                        >
                            <MediaPlayer
                                videoTrack={localVideoTrack}
                                audioTrack={undefined}
                                isVet={false}
                            />
                        </div>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Container maxWidth="md">
                        <Box marginTop="50px" marginBottom="170px">
                            <Grid container direction="column" spacing={4}>
                                <Grid item>
                                    <Box marginBottom="24px">
                                        <Typography variant="h3">
                                                Select Camera
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column">
                                        {
                                            videoDevices.map((item, idx) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            key={idx}
                                                            checked={item.deviceId === videoDevice}
                                                            value={item.deviceId}
                                                            icon={<CheckboxBorder/>}
                                                            checkedIcon={<CheckboxChecked/>}
                                                            onChange={(event) => updateVideoInput(event.target.value)}
                                                        />
                                                    }
                                                    label={item.label}
                                                />
                                            ))
                                        }
                                    </Box>
                                </Grid>
                                <Grid item>
                                    <Box marginBottom="24px">
                                        <Typography variant="h3">
                                                Select Audio
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexWrap="wrap">
                                        {
                                            audioDevices.map((item, idx) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            key={idx}
                                                            checked={item.deviceId === audioDevice}
                                                            value={item.deviceId}
                                                            icon={<CheckboxBorder/>}
                                                            checkedIcon={<CheckboxChecked/>}
                                                            onChange={(event) => updateAudioInput(event.target.value)}
                                                        />
                                                    }
                                                    label={item.label}
                                                />
                                            ))
                                        }
                                    </Box>
                                </Grid>
                                <Grid item>
                                    <Box marginBottom="24px">
                                        <Typography variant="h3">
                                                Verify Sound
                                        </Typography>
                                    </Box>
                                    <Box display="flex">
                                        <Box marginRight="8px">Input Level</Box>
                                        <Box>
                                            {
                                                (() => {
                                                    const volume = [];
                                                    for (let i = 0; i < 11; i++) {
                                                        if (i === level) {
                                                            volume.push(<CheckboxChecked/>);
                                                        } else volume.push(<CheckboxBorder/>);
                                                    }
                                                    return volume;
                                                })()
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} className={classes.buttonContainer}>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        variant="contained"
                                        onClick={enterChat}
                                        fullWidth
                                    >
                                            Enter Appointment Room
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                className={classes.modal}
                open={showContactUs}
                onClose={() => setShowContactUs(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showContactUs}>
                    <div className={classes.paper}>
                        <Typography variant="h2" gutterBottom>
                                Contact Us
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                                Hours: <span style={{fontWeight: 400}}>9am - midnight EST</span>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                                Email: <a aria-label="email support at hello@thevideovets.com" href="mailto:hello@thevideovets.com"
                                style={{fontWeight: 400, textDecoration: "none"}}>hello@thevideovets.com</a>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                                Phone <span style={{fontWeight: 400}}>(Call or Text): <a aria-label="call or text 9172590518" href="tel:9172590518" style={{
                                fontWeight: 400,
                                textDecoration: "none"
                            }}>(917) 259 - 0518</a></span>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                                Chat <span style={{fontWeight: 400}}>If you don't see a chat icon in the bottom right, please try refreshing your browser.</span>
                        </Typography>
                        <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            className={classes.button}
                            onClick={() => setShowContactUs(false)}
                        >
                                Close
                        </Button>
                        <Typography variant="caption">
                                In the case of a life threatening emergency,
                        </Typography>
                        <Typography variant="caption" gutterBottom>
                                seek care at the closest emergency clinic.
                        </Typography>
                    </div>
                </Fade>
            </Modal>
        </Dialog>
    );
}
;
export default SoundAndCameraCheck;
